import axios from 'axios';
import Config from 'context/config';
import Session from 'context/session';
import Navigation from 'context/nav';
import Title from 'context/title';
import { clearFileCache } from 'blob_types/blobhub/compute/workflow/playground/widgets/common/fileBlocks';

export default class Api {
  static EXPIRY_CHECK_INTERVAL_MS = 30 * 1000;

  constructor(state, setAppState, getAppState) {
    this.setAppState = setAppState;
    this.getAppState = getAppState;

    // Currently supported API version
    this.api_version = "v1";

    // Default client for basic operations
    this.axios = axios.create({
      baseURL: Config.apiBaseUrl()
    });

    // Default client for basic authenticated operations
    this.isAuthenticating = false;
    this.authSubscribers = [];
    this.axiosAuth = axios.create({
      baseURL: Config.apiBaseUrl()
    });
    this.axiosAuth.interceptors.request.use((config) => { return this.auth_header_interceptor(config) }, null);

    // Client capable of refreshing token in response to auth errors
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.axiosRefresh = axios.create({
      baseURL: Config.apiBaseUrl()
    });
    this.axiosRefresh.interceptors.request.use((config) => { return this.auth_header_interceptor(config) }, null);
    this.axiosRefresh.interceptors.response.use(null, (error) => { return this.authErrorInterceptor(error) });

    // Users cache
    this.usersCacheById = {};
    this._currentUserIdPromise = null;
    this._ensureSessionIdentityPromise = null;
    this.expiryTimer = null;

    // Load locally cached authentication state
    this.loadLocalAuthState(state);
  }

  log(...args) {
    if ( Config.debug ) {
      console.log(...args);
    }
  }

  /**
   * Public state controls
   */

  loadLocalAuthState(state) {
    const auth = Session.readActive();
    if ( auth ) {
      state.authenticated = true;
      state.auth = auth;
      if ( Session.isActing(auth) ) {
        Title.setActingLabel(auth.name);
      }
    }
  }

  signout() {
    Title.setActingLabel(null);
    return this.unAuthenticate();
  }

  /**
   * Acting mode
   *
   * Entering parks the human and activates a freshly minted, non-renewable session for the account.
   * Leaving restores the parked human. Both go through a full document load, so nothing on screen
   * survives an identity change.
   */

  startActing(account) {
    // The store, not React state — see hasDerivedSessionExpired() for why React state is not to be
    // trusted here. This read only decides whether to ask at all; what gets parked is re-read below.
    const human = Session.readActive();

    // The API refuses to mint an acting token from a session that is already acting, so switching
    // straight from one account to another is impossible: you return to the human first. Refused
    // here as well, so the console never provokes a 403 it could have predicted.
    if ( !Session.isRenewable(human) ) {
      return Promise.reject(new Error("cannot_act_from_a_derived_session"));
    }

    return this.authCoreImpersonatePost(account.id)
      .then(response => {
        const acting = {
          anonymous: false,
          refreshToken: null,
          accessToken: response.data["access_token"],
          expiresAt: response.data["expires_at"],
          user_id: account.id,
          name: account.name,
          type: account.type
        };

        // Re-read rather than park the snapshot taken before the mint: minting is a network round
        // trip, and the human's session can have been hydrated with its identity or refreshed while
        // it was in flight. Parking the snapshot would park a stale copy of it — and a parked human
        // with no user_id is one the menu cannot name and readParked() cannot reconcile.
        const parkable = Session.readActive();
        if ( parkable ) {
          Session.park(parkable, acting);
        } else {
          // Nothing left to return to (a sign-out landed mid-mint): there is nothing to park.
          Session.writeActive(acting);
        }
        this.clearIdentityCaches();
        Title.setActingLabel(acting.name);

        return this.setAppState({ authenticated: true, auth: acting })
          .then(() => {
            Navigation.assign(Navigation.selfUserProfilePath());
          });
      });
  }

  /**
   * @param notify true when the session ended on its own (expiry, or a 401) and the user must be
   *               told; false for the explicit Return control, which reloads immediately.
   *
   * IDEMPOTENT BY DESIGN, and the stored active session is what makes it so. Two requests dispatched
   * in the same tick both reach this through the interceptor, and a second browser tab may have
   * restored the human already. React state cannot arbitrate: under React 18 batching it still holds
   * the acting session after the first caller has written the human to localStorage. So a second
   * caller reading it would restore from an emptied park, get null, and fall into unAuthenticate() —
   * deleting the very human session the first caller just put back, and signing the user out of
   * BlobHub. Once the stored session is absent or renewable, the work is already done.
   */
  stopActing(notify) {
    const ended = Session.readActive();
    if ( !ended || Session.isRenewable(ended) ) {
      return Promise.resolve();
    }

    const restored = Session.restore();

    Title.setActingLabel(null);
    this.clearIdentityCaches();

    if ( !restored ) {
      // Nothing to return to — the only honest outcome is an ended session.
      return this.unAuthenticate();
    }

    return this.setAppState({ authenticated: true, auth: restored })
      .then(() => {
        if ( !notify ) {
          Navigation.reload();
          return undefined;
        }
        return this.setAppState({
          sessionNotice: {
            endedName: ended ? ended.name : null,
            restoredName: restored.name
          }
        });
      });
  }

  /**
   * State management
   */

  authenticate(anonymous, refreshToken, accessToken, expiresAt) {
    const auth = {
      anonymous: anonymous,
      refreshToken: refreshToken,
      accessToken: accessToken,
      expiresAt: expiresAt
    };

    Session.writeActive(auth);

    // Update locally. setAppState is declared (state) => Promise in App: a second-argument callback
    // is silently dropped, so the promise it returns is the only thing that ever settles.
    return this.setAppState({
      authenticated: true,
      auth: auth
    });
  }

  refreshToken(accessToken, expiresAt) {
    // Spread, not rebuild: the session carries user_id / name / type, and rebuilding from two named
    // fields would silently drop the identity the chrome renders.
    //
    // Spread the STORED session, not the React one. The refresh POST is awaited, so a stopActing can
    // swap the session while it is in flight, and React state still holds the pre-swap value for the
    // rest of that tick (batching — see hasDerivedSessionExpired). Merging it would write the
    // PREVIOUS session's user_id / name / type, and because an acting session spreads
    // `refreshToken: null`, would leave a session that can never be refreshed holding a freshly
    // minted human access token.
    const auth = {
      ...Session.readActive(),
      accessToken: accessToken,
      expiresAt: expiresAt
    };

    Session.writeActive(auth);

    return this.setAppState({
      authenticated: true,
      auth: auth
    });
  }

  unAuthenticate() {
    // Ends every session, active and parked: signing out of BlobHub must not leave a human parked
    // behind an ended acting session.
    Session.clearAll();
    this.clearIdentityCaches();

    return this.setAppState({
      authenticated: false,
      auth: null
    });
  }

  clearIdentityCaches() {
    // usersIdGet memoises by id and /users/me is cached under the literal key "me", so a switch that
    // did not clear this would render the previous identity's profile.
    this.usersCacheById = {};
    this._currentUserIdPromise = null;
    this._ensureSessionIdentityPromise = null;
    // fileBlocks.js's resolved-file cache is identity-scoped the same way: a cached blob: URL stands
    // in for an authorization check on that session object, so it must not survive past the identity
    // it was resolved for either.
    clearFileCache();
  }

  updateActiveSession(fields) {
    // Onto the stored session, for the reason spelled out on refreshToken: every caller reaches here
    // from a promise continuation, which is precisely where React state is behind the store.
    const auth = { ...Session.readActive(), ...fields };
    Session.writeActive(auth);
    return this.setAppState({ authenticated: true, auth: auth }).then(() => auth);
  }

  /**
   * Fills user_id / name / type on a session that has none, so the chrome can render the current
   * identity without a fetch. This is also the whole of the migration: a session stored before this
   * feature simply has no identity yet, and gets one the first time the menu asks.
   *
   * The in-flight fetch is memoised like `getCurrentUserId`'s `_currentUserIdPromise`, so overlapping
   * callers (the menu re-hydrates on every relevant re-render) share one `/users/me` request instead
   * of firing one apiece. Unlike that cache, this one clears on EITHER outcome, not only on failure:
   * a success sets `user_id`, so the guard above short-circuits every later call on its own, and a
   * failure must not pin the session to a fetch that never happened — the next real attempt should
   * hit the network again.
   */
  ensureSessionIdentity() {
    // The STORE decides whether there is anything to do, not React state: a hydration that already
    // completed writes the identity to localStorage and only QUEUES its setState, so a React-state
    // guard sees no user_id and fires a second, pointless /users/me for a session that already has
    // one — the very race the swap check below then has to defend against.
    const auth = Session.readActive();
    if ( !auth || auth.anonymous || auth.user_id ) {
      return Promise.resolve(auth || null);
    }
    if ( this._ensureSessionIdentityPromise ) {
      return this._ensureSessionIdentityPromise;
    }
    // `auth` is the session this fetch is FOR. The guard above ran when the call was made; the merge
    // below runs when the network answers, and startActing can have swapped the active session in
    // between. Merging then would stamp the HUMAN's user_id / name / type onto the ACTING session —
    // and because readParked() heals by dropping any parked entry whose user_id matches the active
    // one, that silently discards the parked human: parkedSession() returns null and the next
    // stopActing finds nothing to restore.
    this._ensureSessionIdentityPromise = this.usersIdGetNoCache("me")
      .then(response => {
        if ( !Api.isSameSession(auth, Session.readActive()) ) {
          // Abandon rather than merge. Nothing is lost: this method clears its own in-flight promise
          // below, so whoever needs the identity next simply asks again.
          return Session.readActive();
        }
        const user = response.data.user;
        return this.updateActiveSession({ user_id: user.id, name: user.name, type: user.type });
      }, _ => {
        // An identity we could not fetch is not a reason to end a working session.
        return this.getAppState().auth;
      })
      .then(result => {
        this._ensureSessionIdentityPromise = null;
        return result;
      });
    return this._ensureSessionIdentityPromise;
  }

  /**
   * Whether two reads of the store are the same session. The access token is minted per session and
   * is the only field guaranteed to be there — a session being hydrated has no user_id yet, which is
   * the whole reason it is being fetched. A refresh mints a new access token and therefore reads as
   * a different session, which is the conservative answer: abandoning a merge is free, applying one
   * to the wrong session is not.
   */
  static isSameSession(one, other) {
    if ( !one && !other ) {
      return true;
    }
    if ( !one || !other ) {
      return false;
    }
    return one.accessToken === other.accessToken;
  }

  isActing() {
    return Session.isActing(this.getAppState().auth);
  }

  parkedSession() {
    const parked = Session.readParked();
    return 0 === parked.length ? null : parked[parked.length - 1];
  }

  /**
   * auth
   */

  appendAuthorizationToken(config) {
    const state = this.getAppState();
    config.headers["Authorization"] = `Bearer ${state.auth.accessToken}`;
  }

  auth_header_interceptor(config) {
    var state = this.getAppState();
    if ( state.authenticated ) {
      // A session with no refresh token cannot be refreshed, so once it has expired the only thing
      // left to do is end it -- before sending a request that is already doomed.
      if ( this.hasDerivedSessionExpired() ) {
        this.stopActing(true);
        return Promise.reject(new Error("session_expired"));
      }

      this.appendAuthorizationToken(config);
      return config;
    } else {
      this.log("[auth_header_interceptor] Initiating anonymous authentication...");

      this.authCognitoAnonymousGet();

      const authSubscribers = new Promise(resolve => {
        this.authSubscribers.push(() => {
          this.log("[auth_header_interceptor] Resolving...");
          this.appendAuthorizationToken(config);
          resolve(config);
        });
      });
      return authSubscribers;
    }
  }

  /**
   * READS THE STORE, NEVER REACT STATE — and this is not a stylistic preference.
   *
   * App's `setAppState` is React's `setState`, and `src/index.js` mounts with `createRoot`, so React
   * 18 automatic batching applies: after a restore has already written the human to localStorage,
   * `getAppState().auth` still returns the acting session for the rest of the tick. Axios request
   * interceptors for everything dispatched in that tick run as microtasks inside exactly that
   * window, so a React-state read makes every one of them end a session that is no longer active.
   * localStorage is written synchronously, so Session.readActive() is never stale.
   *
   * Do not "simplify" this back to this.getAppState().auth.
   */
  hasDerivedSessionExpired() {
    const auth = Session.readActive();
    return !!auth && !Session.isRenewable(auth) && Session.isExpired(auth, Date.now());
  }

  /**
   * The one place that turns "has it expired?" into "end it": checks hasDerivedSessionExpired() and,
   * if it has, calls the idempotent stopActing(true). The 30s idle timer below and the acting pill's
   * live countdown both reach zero on the same session and must not each carry their own copy of
   * this pair — one drifting out of sync with the other is exactly the kind of bug two copies invite.
   */
  checkAndEndExpiredSession() {
    if ( this.hasDerivedSessionExpired() ) {
      this.stopActing(true);
    }
  }

  /**
   * The same check on a timer, so an idle tab never keeps claiming an identity that has expired.
   * Driven by App's lifecycle rather than the constructor, so tests and unmounts can stop it.
   */
  startExpiryTimer() {
    this.stopExpiryTimer();
    this.expiryTimer = setInterval(() => {
      this.checkAndEndExpiredSession();
    }, Api.EXPIRY_CHECK_INTERVAL_MS);
  }

  stopExpiryTimer() {
    if ( this.expiryTimer ) {
      clearInterval(this.expiryTimer);
    }
    this.expiryTimer = null;
  }

  authErrorInterceptor(error) {
    this.log("[authErrorInterceptor] Entry point: " + error);

    if ( !error.response ) {
      return Promise.reject(error);
    }

    // Extract message and make it available as top level property for convenience
    this.appendErrorMessage(error);

    const { config, response: { status } } = error;
    const originalRequest = config;

    if ( 401 === status ) {
      // A session with no refresh token cannot be refreshed: a 401 here means the acting token died,
      // so the parked human is restored rather than a refresh attempted. Without this the refresh
      // below POSTs refresh_token: undefined, fails, and signs the human out of BlobHub entirely.
      //
      // From the STORE, not React state, for the reason spelled out on hasDerivedSessionExpired():
      // several in-flight requests answer 401 within one batching window, and a stale read would
      // send every one of them down this branch after the human is already back.
      const auth = Session.readActive();
      if ( auth && !Session.isRenewable(auth) ) {
        this.stopActing(true);
        return Promise.reject(error);
      }

      this.log("[authErrorInterceptor] Initiating refresh...");

      if ( !this.isRefreshing ) {
        this.log("[authErrorInterceptor] Initial refresh request observed");

        this.isRefreshing = true;
        this.authCognitoRefreshPost().then(response => {
          this.log("[authErrorInterceptor] Notifying refresh subscribers...");

          this.isRefreshing = false;
          this.refreshSubscribers.map(callback => callback());
          this.refreshSubscribers = [];
        }, error => {
          this.log("[authErrorInterceptor] Cleaning up subscribers upon failure to refresh...");

          this.isRefreshing = false;
          this.refreshSubscribers = [];
        });
      }
      const requestSubscribers = new Promise(resolve => {
        this.refreshSubscribers.push(() => {
          resolve(this.axiosAuth(originalRequest));
        });
      });
      return requestSubscribers;
    }
    return Promise.reject(error);
  }

  authCognitoRefreshPost() {
    let that = this;
    this.log("[authCognitoRefreshPost] Refreshing access token...");

    // The credential comes from the store as well, and for the sharpest version of the same reason:
    // the 401 branch above reads the store to decide that this session CAN be refreshed, so reading
    // React state here could contradict that decision within the same tick. After a stopActing the
    // stale read is an acting session, whose refreshToken is null — which POSTs refresh_token: null,
    // fails, and calls unAuthenticate(), signing the human out of BlobHub. That is exactly the
    // outcome the branch above exists to prevent.
    const active = Session.readActive();

    return new Promise((resolve, reject) => {
      this.axios.post(`/${this.api_version}/auth/core/refresh`, {
        "refresh_token": active ? active.refreshToken : null
      })
        .then( response => {
          this.log("[authCognitoRefreshPost] Access token obtained");

          // Remember new token
          const accessToken = response.data["access_token"];
          const expiresAt = response.data["expires_at"];
          this.refreshToken(accessToken, expiresAt).then(() => {
            // Complete request
            resolve(response);
          });
        }, error => {
          this.log("[authCognitoRefreshPost] Failed to refresh access token. Signing out...");

          // Sign out completely
          this.unAuthenticate().then(() => {
            // Complete request with failure
            reject(error);
          });
        })
        .catch(function (error) {
          that.log("[authCognitoRefreshPost] Failed to refresh token with generic error: " + error);
        });
    });
  }

  authCognitoAnonymousGet() {
    if ( this.isAuthenticating ) {
      this.log("[authCognitoAnonymousGet] Authentication is already in progress");
      return;
    }

    this.log("[authCognitoAnonymousGet] Initial auth request observed");

    this.isAuthenticating = true;
    this.authCognitoAnonymousGetCore().then(response => {
      this.log("[authCognitoAnonymousGet] Notifying auth subscribers...");

      this.isAuthenticating = false;
      this.authSubscribers.map(callback => callback());
      this.authSubscribers = [];
    }, error => {
      this.log("[authCognitoAnonymousGet] Cleaning up subscribers upon failure to auth...");

      this.isAuthenticating = false;
      this.authSubscribers = [];
    });
  }

  authCognitoAnonymousGetCore() {
    this.log("[authCognitoAnonymousGetCore] Fetching anonymous token...");

    return new Promise((resolve, reject) => {
      this.axios.get(`/${this.api_version}/auth/core/anonymous`)
        .then( response => {
          this.log("[authCognitoAnonymousGetCore] Anonymous token acquired");

          // Remember the token
          const refreshToken = response.data["refresh_token"];
          const accessToken = response.data["access_token"];
          const expiresAt = response.data["expires_at"];
          this.authenticate(true, refreshToken, accessToken, expiresAt).then(() => {
            // Complete request
            resolve(response);
          });
        }, error => {
          this.log("[authCognitoAnonymousGetCore] Failed to fetch anonymous token");

          // Sign out completely
          this.unAuthenticate();

          // Complete request with failure
          reject(error);
        })
        .catch(function (error) {
          this.log("[authCognitoAnonymousGetCore] Failed to fetch anonymous token with generic error: " + error);
        });
    });
  }

  // The token is the whole request. The identity fields this used to send -- external_id, name, email --
  // were what the backend built the account lookup from, so anyone who could pick them could pick whose
  // account signed in. They are read from Google server-side now, and the endpoint rejects a body carrying
  // them at all.
  authGoogleSigninPost(accessToken) {
    return new Promise((resolve, reject) => {
      this.log("[authGoogleSigninPost] Initiating token exchange...");

      const auth = {
        access_token: accessToken
      };
      this.axios.post(`/${this.api_version}/auth/google/signin`, auth)
        .then( response => {
          this.log("[authGoogleSigninPost] Token acquired");

          // Remember the token
          const refreshToken = response.data["refresh_token"];
          const accessToken = response.data["access_token"];
          const expiresAt = response.data["expires_at"];
          this.authenticate(false, refreshToken, accessToken, expiresAt);

          // Complete request
          resolve(response);
        }, error => {
          this.log("[authGoogleSigninPost] Failed to exchange token");
          reject(error);
        });
    });
  }

  /**
   * helpers
   */

  appendErrorMessage(error) {
    let message = "Unexpected error occurred";
    if ( error && error.response && error.response.data && error.response.data.message ) {
      message = error.response.data.message;
    }
    error.message = message;
  }

  /**
   * Users
   */

  usersIdGet(userId) {
    return new Promise((resolve, reject) => {
      const cachedUser = this.usersCacheById[userId];
      if (cachedUser) {
        resolve(cachedUser);
        return;
      }

      this.axiosRefresh.get(`/${this.api_version}/users/${userId}`)
        .then( response => {
          this.usersCacheById[userId] = response;
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  usersIdGetNoCache(userId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/users/${userId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  getCurrentUserId() {
    if (this._currentUserIdPromise) return this._currentUserIdPromise;
    this._currentUserIdPromise = this.usersIdGet("me")
      .then(response => {
        // /v1/users/me returns the user record under response.data.user
        // (same shape as usersIdGet for any other user id — confirmed via
        // existing callers, e.g. components/profile/access.js:166).
        const data = response.data;
        const user = (data && data.user) ? data.user : data;
        return user && user.id ? user.id : null;
      })
      .catch(err => {
        this._currentUserIdPromise = null;
        throw err;
      });
    return this._currentUserIdPromise;
  }

  usersIdPatch(userId, user) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.patch(`/${this.api_version}/users/${userId}`, user)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  users_id_orgs_get(userId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/users/${userId}/orgs`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  usersIdLimitsGet(userId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/users/${userId}/limits`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  isSelfUser(user) {
    return "owner" === user.role;
  }

  /**
   * Service accounts
   *
   * An account is a user record the API returns from the same /users paths, discriminated by `type`.
   * Listing and creating require ADMIN over the OWNER; retiring and impersonating require ADMIN over
   * the ACCOUNT, which the API resolves through its owner.
   */

  usersTargetTargetIdGet(target, targetId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/users/target/${target}/${targetId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  usersTargetTargetIdPost(target, targetId, user) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/users/target/${target}/${targetId}`, user)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  usersIdDelete(userId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/users/${userId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  authCoreImpersonatePost(userId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/auth/core/impersonate`, { user_id: userId })
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Credentials
   */

  credentialsTargetTargetIdGet(target, targetId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/credentials/target/${target}/${targetId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  credentialsTargetTargetIdPost(target, targetId, credential) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/credentials/target/${target}/${targetId}`, credential)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  credentialsIdDelete(credentialId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/credentials/${credentialId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  credentialsManifestGet() {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/credentials/manifest`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Organizations
   */

  orgsPost(org) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/orgs`, org)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdGet(orgId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/orgs/${orgId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdPatch(orgId, org) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.patch(`/${this.api_version}/orgs/${orgId}`, org)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdBlobsGet(orgId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/orgs/${orgId}/blobs`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsPost(orgId, blob) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/orgs/${orgId}/blobs`, blob)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdLimitsGet(orgId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/orgs/${orgId}/limits`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  orgsIdDelete(orgId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/orgs/${orgId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  canAdministerOrg(org) {
    return "admin" === org.role || "owner" === org.role;
  }

  canAccessOrgSettings(org) {
    return !!org.role;
  }

  /**
   * Blobs
   */

  blobsIdGet(orgId, blobId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/blobs/${orgId}/${blobId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdPatch(orgId, blobId, blob) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.patch(`/${this.api_version}/blobs/${orgId}/${blobId}`, blob)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdLimitsGet(orgId, blobId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/blobs/${orgId}/${blobId}/limits`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdRevisionsGet(orgId, blobId, options) {
    const parameters = new URLSearchParams();
    if (options && options.limit) parameters.set("limit", options.limit);
    if (options && options.cursor) parameters.set("cursor", options.cursor);
    const query = parameters.toString();
    return this.axiosRefresh.get(
      `/${this.api_version}/blobs/${orgId}/${blobId}/revisions${query ? `?${query}` : ""}`);
  }

  blobsIdRevisionsPost(orgId, blobId, comment) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/blobs/${orgId}/${blobId}/revisions`, {comment})
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobsIdDelete(orgId, blobId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/blobs/${orgId}/${blobId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  isBlobSettingsAccessible(blob) {
    return "admin" === blob.role || "owner" === blob.role;
  }

  canAccessBlobSettings(blob) {
    return !!blob.role;
  }

  hasBlobReachedTerminalSuccessStatus(blob) {
    return "ready" === blob.status;
  }

  isBlobInUpdatingStatus(blob) {
    return "updating" === blob.status;
  }

  isBlobInDeletingStatus(blob) {
    return "deleting" === blob.status;
  }

  hasBlobReachedTerminalFailureStatus(blob) {
    return "failed" === blob.status;
  }

  /**
   * Blob Metadata
   */

  blobsIdMetadataAliasGet(orgId, blobId, alias) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/blobs/${orgId}/${blobId}/metadata/${alias}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Members
   */

  membersTargetTargetIdPost(target, targetId, member) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/members/target/${target}/${targetId}`, member)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  membersTargetTargetIdGet(target, targetId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/members/target/${target}/${targetId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  membersIdDelete(memberId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/members/${memberId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Blob API Keys
   */

  apiKeysTargetTargetIdGet(target, targetId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/api-keys/target/${target}/${targetId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  apiKeysTargetTargetIdPost(target, targetId, apiKey) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/api-keys/target/${target}/${targetId}`, apiKey)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  apiKeysIdDelete(apiKeyId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/api-keys/${apiKeyId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  blobWriteRoles = new Set(["owner", "admin", "write"]);

  haveBlobWriteAccess(blob) {
    return this.blobWriteRoles.has(blob.role);
  }

  /**
   * Revisions
   */

  revisionsIdGet(revisionId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/revisions/${revisionId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdReopenPost(revisionId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/reopen`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdResetPost(revisionId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/reset`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdDataCommandPost(revisionId, body, data) {
    let headers = {}
    if ( data ) {
      // Header values are ByteStrings — setRequestHeader throws on anything outside ISO-8859-1.
      // body can carry arbitrary user text (e.g. an attachment's filename), so escape every non-ASCII
      // code point to a \uXXXX sequence before it reaches the header; \uXXXX is ordinary JSON, so the
      // server decodes the original string with no contract change.
      headers = {
        "Content-Type": "application/octet-stream",
        "X-Request-Body": JSON.stringify(body).replace(
          // eslint-disable-next-line no-control-regex -- \u0080 is a literal range bound, not a stray control char
          /[\u0080-\uffff]/g, (c) => "\\u" + c.charCodeAt(0).toString(16).padStart(4, "0")
        )
      };
    } else {
      headers = {
        "Content-Type": "application/json"
      };
      data = body;
    }
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/data/command`, data, {
        headers: headers
      })
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdDataQueryPost(revisionId, body) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/data/query`, body, {
        headers: {
          "Accept": "application/octet-stream"
        },
        responseType: "arraybuffer"
      })
        .then( response => {
          if ( "application/octet-stream" === response.headers["content-type"] ) {
            response.binary = response.data;
            response.data = JSON.parse(response.headers["x-response-body"]);
          } else if ( "application/json" === response.headers["content-type"] ) {
            const decoder = new TextDecoder("utf-8");
            const decoded = decoder.decode(response.data);
            response.data = JSON.parse(decoded);
          }
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Session thread items (blobhub.compute.workflow)
   */

  threadItemPost(revisionId, sessionId, alias, content, parentId, metadata) {
    const body = {
      command: "post_session_thread_item",
      session_id: sessionId,
      alias,
      content
    };
    if (parentId !== undefined && parentId !== null) body.parent_id = parentId;
    if (metadata !== undefined && metadata !== null) body.metadata = metadata;
    return this.revisionsIdDataCommandPost(revisionId, body);
  }

  threadItemsList(revisionId, sessionId, alias, options) {
    const body = {
      command: "list_session_thread_items",
      session_id: sessionId,
      alias
    };
    const opts = options || {};
    if (opts.ascending !== undefined) body.ascending = opts.ascending;
    if (opts.createdSince !== undefined) body.created_since = opts.createdSince;
    if (opts.createdBefore !== undefined) body.created_before = opts.createdBefore;
    if (opts.limit !== undefined) body.limit = opts.limit;
    if (opts.cursor) body.cursor = opts.cursor;
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  threadItemGet(revisionId, sessionId, alias, itemId) {
    const body = {
      command: "get_session_thread_item",
      session_id: sessionId,
      alias,
      item_id: itemId
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  sessionFileUpload(revisionId, sessionId, alias, name, contentType, bytes) {
    const body = {
      command: "upload_session_file",
      session_id: sessionId,
      alias,
      name,
      content_type: contentType,
      // Required by the server, and checked against the received byte count (session_file_size_mismatch).
      // Derived from the bytes themselves so the declared size can never disagree with what is sent.
      size: bytes.byteLength
    };
    return this.revisionsIdDataCommandPost(revisionId, body, bytes);
  }

  sessionFileDownload(revisionId, sessionId, alias) {
    const body = {
      command: "download_session_file",
      session_id: sessionId,
      alias
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  /**
   * Session graph (blobhub.compute.workflow)
   */

  applySessionGraphMutations(revisionId, sessionId, alias, operations) {
    const body = {
      command: "apply_session_graph_mutations",
      session_id: sessionId,
      alias,
      operations
    };
    return this.revisionsIdDataCommandPost(revisionId, body);
  }

  querySessionGraph(revisionId, sessionId, alias, query) {
    const body = {
      command: "query_session_graph",
      session_id: sessionId,
      alias,
      query
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  listSessionGraphElements(revisionId, sessionId, alias, options) {
    const body = {
      command: "list_session_graph_elements",
      session_id: sessionId,
      alias
    };
    const opts = options || {};
    if (opts.type !== undefined) body.type = opts.type;
    if (opts.ascending !== undefined) body.ascending = opts.ascending;
    if (opts.cursor) body.cursor = opts.cursor;
    if (opts.updated_since !== undefined) body.updated_since = opts.updated_since;
    if (opts.ids_only !== undefined) body.ids_only = opts.ids_only;
    if (opts.limit !== undefined) body.limit = opts.limit;
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  listSessionGraphNeighbors(revisionId, sessionId, alias, from, options) {
    const body = {
      command: "list_session_graph_neighbors",
      session_id: sessionId,
      alias,
      from
    };
    const opts = options || {};
    if (opts.direction !== undefined) body.direction = opts.direction;
    if (opts.label !== undefined) body.label = opts.label;
    if (opts.neighbor_label !== undefined) body.neighbor_label = opts.neighbor_label;
    if (opts.cursor) body.cursor = opts.cursor;
    if (opts.limit !== undefined) body.limit = opts.limit;
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  getSessionGraphElement(revisionId, sessionId, alias, elementId) {
    const body = {
      command: "get_session_graph_element",
      session_id: sessionId,
      alias,
      element_id: elementId
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  getSessionGraphElements(revisionId, sessionId, alias, elementIds) {
    const body = {
      command: "get_session_graph_elements",
      session_id: sessionId,
      alias,
      element_ids: elementIds
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  // `comment` is optional and replaces the draft's. A blank one keeps the draft's, and the schema has
  // minLength 1 -- so blank must send no key at all, not the "" JSON.stringify would otherwise keep.
  revisionsIdCommitPost(revisionId, comment) {
    const kept = (comment || "").trim();
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/commit`, kept ? {comment: kept} : {})
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdPatch(revisionId, revision) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.patch(`/${this.api_version}/revisions/${revisionId}`, revision)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdDelete(revisionId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/revisions/${revisionId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  // 13 call sites: the workflow editor, playground, debugger, scheduler schedules, graph explore
  // (query and appearance), the ONNX shell, the markdown page, and revisions.js's Edit. Each writes
  // through its own endpoint with its own status contract -- this predicate carries none of them, on
  // purpose, so tightening it for one caller cannot silently freeze the other twelve.
  canUpdateRevision(blob, revision) {
    return ( this.haveBlobWriteAccess(blob) && ("draft" === revision.phase || "managed" === revision.phase) );
  }

  // PATCH's own contract: STATUSES_WRITE is {ready}, PHASES_PATCH is {draft, managed}. This is
  // revisions.js's Edit control's only predicate -- the other twelve canUpdateRevision callers write
  // through different endpoints and must not inherit a status rule that is PATCH's alone.
  canEditRevisionRecord(blob, revision) {
    return ( this.canUpdateRevision(blob, revision) && "ready" === revision.status );
  }

  canCommitRevision(blob, revision) {
    return ( this.haveBlobWriteAccess(blob) && "draft" === revision.phase && "ready" === revision.status );
  }

  canCreateRevision(blob, revision) {
    return (
      this.haveBlobWriteAccess(blob) &&
      revision.id === blob.default_revision_id &&
      "commit" === revision.phase &&
      "ready" === revision.status
    );
  }

  canDeleteRevision(blob, revision) {
    return (
      this.isBlobSettingsAccessible(blob) &&
      blob.default_revision_id !== revision.id &&
      ("failed" === revision.status || ("commit" === revision.phase && "ready" === revision.status))
    );
  }

  // Discard and Delete are one call; a draft with a parent is the discard case. `parent_id` is what
  // keeps the initial revision undiscardable -- the last-revision case is a 409 the row renders.
  canDiscardRevision(blob, revision) {
    return (
      this.isBlobSettingsAccessible(blob) &&
      "draft" === revision.phase &&
      "ready" === revision.status &&
      !!revision.parent_id
    );
  }

  canReopenRevision(blob, revision) {
    return (
      this.haveBlobWriteAccess(blob) &&
      revision.id === blob.default_revision_id &&
      "commit" === revision.phase &&
      "ready" === revision.status
    );
  }

  canResetRevision(blob, revision) {
    return ( this.haveBlobWriteAccess(blob) && "failed" === revision.status );
  }

  // Retry re-issues the call that failed, so it inherits that call's role. Only a discard or delete is
  // retryable -- commit refuses a failed revision, which Reset is for -- so in practice this is the
  // admin-gated DELETE; the commit arm remains for the role rule rather than for a reachable caller.
  canRetryRevision(blob, revision, operation) {
    return "commit" === operation
      ? this.canResetRevision(blob, revision)
      : (this.isBlobSettingsAccessible(blob) && "failed" === revision.status);
  }

  hasRevisionReachedTerminalStatus(revision) {
    return ("ready" === revision.status || "failed" === revision.status);
  }

  /**
   * Revision Metadata
   */

  revisionsIdMetadataAliasGet(revisionId, alias) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/revisions/${revisionId}/metadata/${alias}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsIdMetadataAliasPost(revisionId, alias, item) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.post(`/${this.api_version}/revisions/${revisionId}/metadata/${alias}`, item)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsMetadataIdPut(itemId, item) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.put(`/${this.api_version}/revisions/metadata/${itemId}`, item)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  revisionsMetadataIdDelete(itemId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.delete(`/${this.api_version}/revisions/metadata/${itemId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  /**
   * Operations
   */

  operationsIdGet(operationId) {
    return new Promise((resolve, reject) => {
      this.axiosRefresh.get(`/${this.api_version}/operations/${operationId}`)
        .then( response => {
          resolve(response);
        }, error => {
          reject(error);
        });
    });
  }

  isOperationStillInProgress(operation) {
    return "in_progress" === operation.status;
  }

  hasOperationCompleted(operation) {
    return "completed" === operation.status;
  }

  hasOperationFailed(operation) {
    return "failed" === operation.status;
  }

  /**
   * Workflow
   */

  workflowManifestGet(revisionId, category) {
    const body = {
      command: "get_manifest",
      category: category
    };
    return this.revisionsIdDataQueryPost(revisionId, body);
  }

  /**
   * Workflow Execution Events
   */

   hasWorkflowExecutionReachedTerminalStatus(execution) {
     return "completed" === execution.status || "failed" === execution.status || "stopped" === execution.status;
   }
}
